/*
 * This file is part of glowingblue/orell-articles.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Modal from 'flarum/common/components/Modal';
import type { IInternalModalAttrs } from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';
import icon from 'flarum/common/helpers/icon';
import Stream from 'flarum/common/utils/Stream';
import withAttr from 'flarum/common/utils/withAttr';
import search from '../search';
import type { ArticleSearchPage, ArticleSearchResult } from '../search';
import ArticleListContent from './ArticleListContent';
import app from 'flarum/forum/app';
import type Article from '../models/Article';
import type Mithril from 'mithril';

/**
 * The picker's selection and search results, handed back to the opener so that
 * reopening the modal can restore them.
 */
export interface ArticlePickerState {
	articles: Article[];
	query: string;
	results: Article[] | false;
}

export interface ArticlePickerModalAttrs extends IInternalModalAttrs {
	pickerState?: ArticlePickerState;
	multiSelectable?: boolean;
	saveState: (state: ArticlePickerState) => void;
}

export default class ArticlePickerModal extends Modal<ArticlePickerModalAttrs> {
	query!: Stream<string>;
	results!: Article[] | false;
	articles!: Article[];
	nextPage: ArticleSearchPage | false = false;
	protected searchTimeout?: number;
	protected observer?: IntersectionObserver;
	protected wasSaved = false;

	oninit(vnode: Mithril.Vnode<ArticlePickerModalAttrs, this>) {
		super.oninit(vnode);

		const { articles, query, results } = this.attrs.pickerState || {};

		this.loading = false;
		this.query = Stream(query ? query : '');
		this.results = results ? results : [];
		this.articles = articles ? articles : [];
	}

	className(): string {
		return 'ArticlePickerModal Modal--large';
	}

	title(): Mithril.Children {
		return app.translator.trans('glowingblue-orell-articles.forum.select_article.title');
	}

	oncreate(vnode: Mithril.VnodeDOM<ArticlePickerModalAttrs, this>) {
		super.oncreate(vnode);

		this.makeSubmitButtonSticky();

		// Inspired from flarum/components/Search.prototype.oncreate

		const $input = this.$('input');

		$input.focus();

		$input.on('input focus', (event) => {
			const query = (event.currentTarget as HTMLInputElement).value.toLowerCase();

			if (!query) return;

			clearTimeout(this.searchTimeout);
			this.searchTimeout = window.setTimeout(() => {
				this._search();
			}, 250);
		});

		// The parent's transform breaks `position: fixed` on the sticky submit button:
		// https://stackoverflow.com/questions/15194313/transform3d-not-working-with-position-fixed-children/15256339
		setTimeout(() => this.$().closest('.ModalManager .Modal').css('transform', 'none'), 200);
	}

	onupdate(vnode: Mithril.VnodeDOM<ArticlePickerModalAttrs, this>) {
		super.onupdate(vnode);

		this.makeSubmitButtonSticky();
	}

	makeSubmitButtonSticky() {
		const $button = this.$('.ArticlePickerModal-submit-wrapper .Button');

		if (this.observer) {
			this.observer.disconnect();
		}

		const node = this.$('#intersect-obs-node')[0];

		if (!node) return;

		this.observer = new IntersectionObserver((entries) => {
			let isIntersecting = false;
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					isIntersecting = true;
				}
			});
			if (isIntersecting) {
				$button.removeClass('is-sticky');
			} else {
				$button.addClass('is-sticky');
			}
		});
		this.observer.observe(node);
	}

	content(): Mithril.Children {
		const { multiSelectable } = this.attrs;
		const hasResults = this.results !== false && this.results.length > 0;

		return [
			<div className="Modal-body">
				<div className="ArticlePickerModal-form">
					<div className="Form-group ArticlePickerModal-searchInput Search-input">
						<input
							className="FormControl"
							value={this.query()}
							oninput={withAttr('value', this.query)}
							placeholder={app.translator.trans('glowingblue-orell-articles.forum.select_article.search_placeholder')}
							type="search"
						/>

						{/* Must stay BEFORE the "clear" button: otherwise pressing ENTER in the
						search field triggers clear instead of submit. */}
						<Button type="submit" className="Button" disabled={this.loading}>
							{app.translator.trans('glowingblue-orell-articles.forum.select_article.search')}
						</Button>

						{this.loading ? (
							<LoadingIndicator size="tiny" />
						) : (
							hasResults && (
								<button className="Search-clear Button Button--icon Button--link" onclick={() => this.clear()}>
									{icon('fas fa-times-circle')}
								</button>
							)
						)}
					</div>

					<div className="Form-group ArticlePickerModal-searchResults-wrapper">
						<div className="ArticlePickerModal-searchResults">
							{this.results !== false && this.results.length > 0 && (
								<ArticleListContent
									articles={this.results}
									onSelect={(article: Article) => this.onSelect(article)}
									multiSelectable={multiSelectable}
									selectedArticles={this.articles}
									searchQuery={this.query()}
									loadMore={
										this.nextPage && (
											<Button onclick={() => this.loadMore()} className="Button Button--secondary" loading={this.loading}>
												{app.translator.trans('core.forum.discussion_list.load_more_button')}
											</Button>
										)
									}
								/>
							)}

							{this.loading && !this.nextPage ? (
								<LoadingIndicator />
							) : this.results === false ? (
								<Placeholder text={app.translator.trans('glowingblue-orell-articles.forum.select_article.nothing_found')} />
							) : (
								this.results.length === 0 && (
									<Placeholder text={app.translator.trans('glowingblue-orell-articles.forum.select_article.nothing_to_show')} />
								)
							)}
						</div>
					</div>

					<div className="Form-group ArticlePickerModal-submit-wrapper">
						<Button className="Button Button--action" onclick={() => this.save()} disabled={!this.articles.length}>
							{app.translator.trans('glowingblue-orell-articles.forum.select_article.select')}
						</Button>
						<div id="intersect-obs-node"></div>
					</div>
				</div>
			</div>,
		];
	}

	onsubmit(e: SubmitEvent) {
		e.preventDefault();
		this._search();
	}

	onSelect(article: Article) {
		const { multiSelectable } = this.attrs;

		if (multiSelectable) {
			if (this.articles.indexOf(article) === -1) {
				this.articles.push(article);
			} else {
				this.articles = this.articles.filter((a) => a !== article);
			}
		} else {
			this.articles = [article];
		}
		m.redraw();
	}

	_search() {
		const query = this.query().toLowerCase();

		// Don't run the search if the results are loaded from a previous state
		if (
			this.attrs.pickerState &&
			this.attrs.pickerState.results &&
			query === this.attrs.pickerState.query &&
			this.results === this.attrs.pickerState.results
		) {
			return;
		}

		if (!query || query.length <= 3) {
			return;
		}

		this.results = [];
		this.loading = true;
		this.nextPage = false;
		// Synchronous, else the loading indicator never appears.
		m.redraw(true);

		search(this.query())
			.then((result) => this.handleSearchSuccess(result))
			.catch((error: unknown) => this.handleSearchError(error));
	}

	loadMore() {
		const nextPage = this.nextPage;

		if (nextPage) {
			this.loading = true;
			nextPage()
				.then((result) => this.handleSearchSuccess(result))
				.catch((error: unknown) => this.handleSearchError(error));
		}
	}

	handleSearchSuccess({ articles, isFirstPage, nextPage }: ArticleSearchResult) {
		if (isFirstPage) {
			this.results = articles;
		} else {
			const previous = this.results === false ? [] : this.results;
			this.results = [...previous, ...(articles === false ? [] : articles)];
		}
		this.nextPage = nextPage;
		this.loading = false;

		m.redraw();
	}

	/**
	 * The rejection reason is forwarded so that `extend()` consumers of this seam can inspect it,
	 * even though the default implementation does not use it.
	 */
	handleSearchError(_error?: unknown) {
		this.loading = false;

		m.redraw();
	}

	clear() {
		this.loading = false;
		this.results = [];
		this.nextPage = false;
		this.query('');

		m.redraw();
	}

	onBeforeSave() {
		// Deliberately empty: a seam for extend() consumers.
	}

	save() {
		this.onBeforeSave();

		this.attrs.saveState({
			articles: this.articles,
			query: this.query(),
			results: this.results,
		});

		this.wasSaved = true;

		app.modal.close();
	}

	onremove(vnode: Mithril.VnodeDOM<ArticlePickerModalAttrs, this>) {
		super.onremove(vnode);

		clearTimeout(this.searchTimeout);

		if (!this.wasSaved) {
			// Closed without saving: keep the picker state anyway, so reopening restores it.
			this.attrs.saveState({
				...this.attrs.pickerState,
				articles: this.attrs.pickerState?.articles ?? [],
				query: this.query(),
				results: this.results,
			});
		}
	}
}
